import { Button, EButtonColorVariant } from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';
import Slider, { Settings as SliderSettings } from 'react-slick';

import Link from '@/components/switchback/Link/Link';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ImageBlockType } from '@/pages/loans';

import css from './GetOuttaHereTemplate.module.css';

const PARTNER_SITE_URLS = [
  'https://www.yeti.com/',
  'https://www.alltrails.com/',
  'https://www.merrell.com/US/en/home',
  'https://roadtrippers.com/',
  'https://harvesthosts.com/',
  'https://www.rvonthego.com/',
];

const FILTERS = {
  typeC: 'c',
  trailers: 'trailer,folding-trailer',
};

const adventureButtonInfoMap = [
  {
    labelText: 'Search Class Cs',
    url: `/rv-search?filter[type]=${encodeURIComponent(FILTERS.typeC)}`,
  },
  {
    labelText: 'Search Trailers',
    url: `/rv-search?filter[type]=${encodeURIComponent(FILTERS.trailers)}`,
  },
];

export interface IGOHProps {
  seoTitle: string;
  seoDescription: string;
  hero_image: string;
  subheading: string;
  gohLogo: ImageBlockType;
  carouselImageUrls: ImageBlockType[];
  partnerCardInfo: IPartnerCardInfo[];
  vehicleCardInfo: IVehicleCardInfo[];
  noindex: boolean;
  button_label: string;
}

export interface IPartnerCardInfo {
  partnerCardImage: ImageBlockType;
  partnerLogo: ImageBlockType;
  partnerHeading: string;
  partnerDescription: string;
}

export interface IVehicleCardInfo {
  primaryHeading: string;
  secondaryHeading: string;
  primaryImage: ImageBlockType;
  secondaryImages: ImageBlockType[];
}

const GetOuttaHereTemplate: React.FC<IGOHProps> = ({
  hero_image,
  subheading,
  gohLogo,
  carouselImageUrls,
  partnerCardInfo,
  vehicleCardInfo,
  button_label,
}) => {
  const intl = useIntl();
  const { isMobile } = useBreakpoint();

  const settings: Partial<SliderSettings> = {
    dots: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 5000,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const vehicleCardMap =
    vehicleCardInfo &&
    vehicleCardInfo.map((i: IVehicleCardInfo, index: number) => {
      return (
        <section className={css.adventureCard} key={index}>
          <div className={css.adventureCardHeadingsContainer}>
            <h6 className={`${css.smallTitle} ${css.gohOrangeSectionTitle}`}>{i.primaryHeading}</h6>
            <h6 className={css.smallTitle}>{i.secondaryHeading}</h6>
          </div>

          <div className={css.vehiclesImageContainer}>
            <div>
              <ResponsiveImage
                src={i.primaryImage.url}
                alt={i.primaryImage.title}
                role="presentation"
              />
            </div>

            <div className="flex flex-1 my-4 gap-4">
              {i.secondaryImages.map(image => (
                <div key={image.url}>
                  <ResponsiveImage src={image.url} alt={image.title} role="presentation" />
                </div>
              ))}
            </div>
          </div>

          <Link className={css.gohLink} href={adventureButtonInfoMap[index]?.url} target="_blank">
            <Button
              className={css.gohButton}
              variant={EButtonColorVariant.Primary}
              label={adventureButtonInfoMap[index]?.labelText}
            />
          </Link>
        </section>
      );
    });

  return (
    <>
      <main className={css.main}>
        <section className={css.hero}>
          <div className={css.heroLeftSection}>
            <div className={css.heroLogo}>
              <ResponsiveImage src={gohLogo.url} alt="get-outta-here-logo" role="presentation" />
            </div>
            <div className={css.heroHeading}>
              <h2>{subheading}</h2>
            </div>
            <Button
              className={`${css.gohButton} ${css.gohCTA} ${css.leftAligned}`}
              id="gohCTA"
              href="/rv-search"
              variant={EButtonColorVariant.Primary}
              label={button_label}
            />
          </div>
          <div className={css.heroRightSection}>
            <ResponsiveImage src={hero_image} alt="get-outta-here-hero" role="presentation" />
          </div>
        </section>

        <section className={`${css.gohSection}`}>
          <div className={`${css.sliderContainer} ${css.maxWidthAlighmentContainer}`}>
            <Slider {...settings}>
              {carouselImageUrls.length > 0 &&
                carouselImageUrls.map(i => (
                  <div className={css.maxWidthAlighmentContainer} key={i.url}>
                    <ResponsiveImage
                      className="w-full h-full"
                      key={i.url}
                      src={i.url}
                      alt={i.title}
                      role="presentation"
                    />
                  </div>
                ))}
            </Slider>
          </div>
        </section>

        <section className={css.partnerSectionsContainer}>
          <section className={css.gohSection}>
            <h3 className={css.gohSectionTitle}>
              {intl.formatMessage({
                defaultMessage: 'Thank you to our partners.',
                id: 'dqBIna',
              })}
            </h3>
            <div className={`${css.gohDescription} ${css.gohSecondaryColor}`}>
              {intl.formatMessage({
                defaultMessage:
                  'We came together with some incredible partners so every Get Outta Here trip winner would also receive an Escape Pack with everything they needed for the ultimate getaway. Check out what was in each pack below:',
                id: 'jZBmjp',
              })}
            </div>
          </section>
          <section className={`${css.maxWidthAlighmentContainer} ${css.partnerSection}`}>
            {partnerCardInfo &&
              partnerCardInfo.map((i: IPartnerCardInfo, index: number) => {
                return (
                  <section
                    className={`${css.splitContent} ${
                      index % 2 !== 0 ? css.splitContentReverse : ''
                    }`}
                    key={i.partnerCardImage.url}>
                    <section>
                      <div className={css.partnerMainImageContainer}>
                        <ResponsiveImage
                          src={i.partnerCardImage.url}
                          alt="main-partner-image"
                          role="presentation"
                        />
                      </div>
                    </section>
                    <section className={css.partnerSectionInfo}>
                      <Link href={PARTNER_SITE_URLS[index]} target="_blank">
                        <ResponsiveImage src={i.partnerLogo.url} alt="partner-logo" />
                      </Link>
                      <h2 className={css.partnerSectionHeading}>{i.partnerHeading}</h2>
                      <div
                        className={`${css.partnerSectionDescription} ${css.gohSecondaryColor}`}
                        dangerouslySetInnerHTML={{ __html: i.partnerDescription }}
                      />
                    </section>
                  </section>
                );
              })}
          </section>
        </section>

        <section className={`${css.adventureSection} ${css.maxWidthAlighmentContainer}`}>
          <h4 className={`${css.adventureFirstSectionTitle} ${css.gohOrangeSectionTitle}`}>
            {intl.formatMessage({
              defaultMessage: 'START YOUR ADVENTURE NOW',
              id: '/fXrs7',
            })}
          </h4>
          <h3 className={css.adventureSecondSectionTitle}>
            {intl.formatMessage({
              defaultMessage: 'Vehicles ready to get you outta here.',
              id: 'vgny3E',
            })}
          </h3>
          <div
            className={`${css.gohDescription} ${css.adventureDescription} ${css.gohSecondaryColor}`}>
            {intl.formatMessage({
              defaultMessage:
                'Whatever adventure you’re dreaming of, we have the vehicle for you. Check out our range of vehicles that suit both thrill seekers and comfort seekers. Take the wheels on the road or have them delivered right to you.',
              id: 'H9zm6J',
            })}
          </div>

          <div className={`${css.adventureCardsContainer} ${css.maxWidthAlighmentContainer}`}>
            {isMobile ? (
              <div className={css.sliderContainer}>
                <Slider {...settings}>{vehicleCardMap}</Slider>
              </div>
            ) : (
              vehicleCardMap
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default GetOuttaHereTemplate;
